import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Building2,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Download,
  Flag,
  LayoutGrid,
  LayoutList,
  GanttChart,
  Repeat,
  ShieldCheck,
  User,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import {
  DateRangeChip,
  FilterBar,
  FilterChip,
} from '../../components/filters/FilterBar'
import { FilterRow } from '../../components/filters/FilterRow'
import { api, extractApiError } from '../../lib/api'
import { invalidateGamification } from '../../lib/gamification-refresh'
import { useRangeSelection } from '../../lib/useRangeSelection'
import { TaskMonitorBulkBar } from './TaskMonitorBulkBar'
import { FloatingXScrollbar } from '../../components/table/FloatingXScrollbar'
import { TaskMonitorGantt } from './TaskMonitorGantt'
import { StatusDot, isUnstartedStatus } from '../../components/StatusDot'
import { AvatarStack } from '../../components/AvatarStack'
import { CommentsCell } from '../../components/TaskComments'
import { neutraliseFormula } from '../../components/table/tableExport.js'
import { useAuthStore } from '../../stores/authStore'
import { formatDateTime, formatDateOnly, formatPattern, formatTime, datePattern, getCalendarTimeZone, dayOffsetFromToday, dayKey } from '../../lib/format'
import { useReportSchedule, workingWeekStartDay } from '../../lib/report-schedule'
import { DateTime } from 'luxon'
import { GitBranch } from 'lucide-react'
import { readableInk } from '../../lib/theme'

/** Render a task's start/due date respecting whether a time was actually
 *  set. A date-only value is stored at midnight UTC, so formatting it WITH
 *  a time renders a spurious "05:30 AM" (in IST). Show the time only when
 *  `hasTime` is true; otherwise a bare date. */
function taskDate(value, hasTime) {
  if (!value) return ''
  return hasTime ? formatDateTime(value) : formatDateOnly(value)
}

/** Date part only, for the dedicated Start/Due Date columns. A date-only
 *  value is the company's calendar day; a value with a time is the day it
 *  falls on in the person's zone, so the date column agrees with the time
 *  column beside it (`taskTime`, also the person's zone). */
function taskDay(value, hasTime) {
  return value && hasTime ? formatPattern(value, datePattern()) : formatDateOnly(value)
}

/** Time part only (e.g. "02:17 PM") for the dedicated Start/Due Time
 *  columns. Empty when the value is date-only (no time was set): such a
 *  value sits at midnight and would otherwise render a spurious time. */
function taskTime(value, hasTime) {
  return value && hasTime ? formatTime(value) : ''
}

const STATUS_CATEGORY_ORDER = [
  'backlog',
  'not_started',
  'active',
  'review',
  'done',
  'closed',
  'cancelled',
]

const PRIORITY_META = {
  urgent: { label: 'Urgent', color: 'var(--color-priority-urgent)' },
  high: { label: 'High', color: 'var(--color-priority-high)' },
  normal: { label: 'Medium', color: 'var(--color-priority-normal)' },
  low: { label: 'Low', color: 'var(--color-priority-low)' },
}

// Checker / verification state — keyed by a task's `approvalStatus`
// (`none` | `pending` | `approved` | `rejected`). Lets an admin filter
// Task Monitor by where each task stands with its Checker.
const VERIFICATION_META = {
  approved: { label: 'Verified', swatch: '#16a34a' },
  pending: { label: 'Awaiting checker', swatch: '#f59e0b' },
  rejected: { label: 'Rejected', swatch: '#dc2626' },
  none: { label: 'No verification', swatch: '#94a3b8' },
}
const VERIFICATION_ORDER = ['approved', 'pending', 'rejected', 'none']

const AVATAR_PALETTE = [
  '#02367B',
  '#34D399',
  '#F87171',
  '#FBBF24',
  '#A78BFA',
  '#F472B6',
  '#0a2349',
  '#FB923C',
]

function avatarColor(name) {
  // Tasks with no assignee surface here as `null` — never crash, just
  // pick a stable fallback colour so the placeholder still has visual
  // weight in the table.
  const safe = typeof name === 'string' && name.length > 0 ? name : '?'
  let hash = 0
  for (let i = 0; i < safe.length; i++) hash = (hash * 31 + safe.charCodeAt(i)) | 0
  const idx = Math.abs(hash) % AVATAR_PALETTE.length
  return AVATAR_PALETTE[idx]
}

function initials(name) {
  if (typeof name !== 'string' || name.trim().length === 0) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Selection checkbox. All toggle logic lives in `onClick` (Shift-range aware)
 *  with an empty `onChange`, so React's onChange can't re-toggle the row and
 *  cancel a Shift-range. Sets the native `indeterminate` flag via ref. */
function SelectCheckbox({ checked, indeterminate = false, onClick, ariaLabel }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={() => {}}
      onClick={onClick}
      aria-label={ariaLabel}
      className="h-4 w-4 cursor-pointer accent-[color:var(--color-primary)] align-middle"
    />
  )
}

function PriorityBadge({ priority }) {
  // Tasks may have a null priority. Render a neutral placeholder
  // instead of throwing on `meta.color`.
  const meta = PRIORITY_META[priority]
  if (!meta) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-[color:var(--color-ink-muted)]">
        <Flag size={12} aria-hidden="true" />
        —
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: meta.color }}>
      <Flag size={12} fill={meta.color} strokeWidth={0} aria-hidden="true" />
      {meta.label}
    </span>
  )
}

function Avatar({ name }) {
  return (
    <span
      className="inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-medium text-white shrink-0"
      style={{ backgroundColor: avatarColor(name) }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  )
}

/** Space-style status pill — the SAME colored, tinted-background pill the
 *  employee Space List/Board use (SpacePage `StatusPill`), so the Task Monitor
 *  reads with the same visual language. The tenant's exact status colour tints
 *  the background (`${color}1f` = 12% alpha) and the dot; the label uses
 *  `readableInk` so a pale workflow colour (e.g. the default TO DO grey) still
 *  clears AA contrast on the tint. */
function StatusPill({ name, color }) {
  const c = color || '#94a3b8'
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold uppercase tracking-wider"
      style={{ color: readableInk(c), backgroundColor: `${c}1f` }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full shrink-0"
        style={{ backgroundColor: c }}
        aria-hidden="true"
      />
      {name}
    </span>
  )
}

/** Urgency colour for a due date — overdue = red, due today = amber, completed
 *  on-time = green, otherwise muted. Mirrors the Space List/Board `DueChip`
 *  (SpacePage `dueDateColor`) so both surfaces convey the same cue. Day offset
 *  is computed in the tenant display timezone. */
function dueDateColor(iso, completedAt) {
  if (!iso) return 'var(--color-ink-muted)'
  const diffDays = dayOffsetFromToday(iso) ?? 0
  if (completedAt) {
    if ((dayKey(completedAt) ?? '') <= (dayKey(iso) ?? ''))
      return 'var(--color-status-complete, #16a34a)'
    return 'var(--color-ink-muted)'
  }
  if (diffDays < 0) return 'var(--color-priority-urgent)'
  if (diffDays === 0) return 'var(--color-priority-high)'
  return 'var(--color-ink-muted)'
}

function ViewTab({ label, active, icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'h-9 px-4 rounded-md inline-flex items-center gap-2 text-sm cursor-pointer transition-colors ' +
        (active
          ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] border border-[color:var(--color-accent-border)]'
          : 'text-[color:var(--color-ink-muted)] border border-transparent hover:bg-[color:var(--color-card-hover)]')
      }
    >
      {icon}
      {label}
    </button>
  )
}

/**
 * CSV export — builds the file CLIENT-SIDE from the already-loaded
 * task list (the same `allTasks` the Task Monitor table renders). No
 * backend round-trip, so:
 *   1. No `/v1/admin/tasks/export.csv` 404 → HTML-fallback bug
 *      (the SPA used to serve `index.html` for unknown routes, and
 *      the browser downloaded that as the "CSV").
 *   2. The download always matches exactly what's on screen — same
 *      filters, same statuses, same multi-assignee + comment counts
 *      the new columns surface.
 *
 * The Import CSV button + dialog were removed: the previous flow
 * pointed at non-existent backend routes (`/admin/tasks/import` and
 * `/admin/tasks/import/preview`) and produced confusing errors. If
 * import comes back later it'll be a different, properly-wired
 * implementation, not the dead stub.
 */
function CsvExportButton({ tasks }) {
  const disabled = !Array.isArray(tasks) || tasks.length === 0
  return (
    <Button
      variant="primary"
      size="sm"
      onClick={() => downloadCsv(tasks ?? [])}
      disabled={disabled}
      className="shrink-0"
      title={
        disabled
          ? 'No tasks to export — refine your filters or wait for the list to load'
          : `Export ${tasks.length} task${tasks.length === 1 ? '' : 's'} (matching the current filters, subtasks included) as CSV`
      }
    >
      <Download size={14} />
      Export CSV
    </Button>
  )
}

function escapeCsv(raw) {
  // Task / assignee / creator names are user-typed — neutralise formula injection.
  const value = neutraliseFormula(raw)
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`
  return value
}

function downloadCsv(tasks) {
  const header = [
    'Task Name',
    'Status',
    'Department',
    'Space',
    'Assignees',
    'Checker',
    'Start Date',
    'Start Time',
    'Due Date',
    'Due Time',
    'Priority',
    'Comments',
    'Created By',
  ]
  const rows = tasks.map((t) => {
    // Multi-assignee: join the ASSIGNEE NAMES (never the profile object /
    // avatar) with " | " inside the cell. Pipe is a safe in-cell separator
    // because the row-level separator is a comma, and `escapeCsv` quotes any
    // cell containing one anyway. Read `fullName ?? name` — the same dual
    // shape AvatarStack renders — so the exported name always matches the
    // on-screen avatar's name even if the row arrives in the member shape.
    const assigneeNames = Array.isArray(t.assignees)
      ? t.assignees
          .map((a) => a?.fullName ?? a?.name ?? '')
          .filter(Boolean)
          .join(' | ')
      : t.assigneeName ?? ''
    return [
      t.name ?? '',
      t.statusName ?? t.status ?? '',
      t.department ?? '',
      t.space?.name ?? '',
      assigneeNames,
      t.verifierName ?? '',
      taskDay(t.startDate, t.startDateHasTime),
      taskTime(t.startDate, t.startDateHasTime),
      taskDay(t.dueDate, t.dueDateHasTime),
      taskTime(t.dueDate, t.dueDateHasTime),
      PRIORITY_META[t.priority]?.label ?? '',
      t.commentCount ?? 0,
      t.createdByName ?? t.createdBy ?? '',
    ]
  })
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => escapeCsv(String(cell))).join(','))
    .join('\n')
  // UTF-8 BOM so Excel opens the file with the right encoding (its
  // default codepage on Windows is cp1252 — without the BOM, names
  // with accented characters render as gibberish).
  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const stamp = new Date().toISOString().slice(0, 10)
  a.download = `taskmagix-tasks-${stamp}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function TaskMonitorPage() {
  const qc = useQueryClient()
  // The shared CommentsCell needs the actor's id so it knows which
  // comments to surface a "Delete" affordance on (a user can only
  // delete their OWN comments).
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const [view, setView] = useState('list')
  // Wraps the List view's status sections so the shared FloatingXScrollbar can
  // find every section's horizontal scroll area (marked `data-fd-xscroll`) and
  // drive a single viewport-pinned scrollbar across them.
  const listScrollRef = useRef(null)
  const [collapsed, setCollapsed] = useState(new Set())
  // Per-parent-task expand state. Set<taskId> of parents whose
  // subtasks are currently visible. Default-collapsed so the table
  // doesn't dump the full tree on initial load — matches the
  // Spaces page's expand-on-click pattern.
  const [expandedParents, setExpandedParents] = useState(() => new Set())
  function toggleParent(taskId) {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }

  // URL-synced multi-select filters. State serialised as comma-separated
  // values in the query string so a filter combination is shareable and
  // back/forward navigation works.
  const [searchParams, setSearchParams] = useSearchParams()
  const readSet = useCallback(
    (key) => {
      const raw = searchParams.get(key)
      if (!raw) return new Set()
      return new Set(raw.split(',').filter(Boolean))
    },
    [searchParams],
  )
  const writeSet = useCallback(
    (key, set) => {
      const next = new URLSearchParams(searchParams)
      if (set.size === 0) next.delete(key)
      else next.set(key, [...set].join(','))
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )
  // Write one or more string params in a SINGLE update. Calling a
  // single-key writer twice in the same handler does NOT work: each call
  // rebuilds from the same render-time `searchParams` snapshot and the
  // second `setSearchParams` wins, silently dropping the first key. The
  // date-range filter sets `dueFrom` + `dueTo` together, so they must be
  // written atomically — otherwise "From", written first, was always
  // clobbered by the "To" write and never stuck (the reported bug).
  const writeStrs = useCallback(
    (pairs) => {
      const next = new URLSearchParams(searchParams)
      for (const [key, value] of Object.entries(pairs)) {
        if (!value) next.delete(key)
        else next.set(key, value)
      }
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  // ── Dashboard shortcut alias normalisation ────────────────────────
  // The various role-panel dashboards link here with friendly aliases
  // (`?filter=overdue`, `?filter=complete`, `?due=today`, etc.) rather
  // than the canonical multi-key shape this page uses internally
  // (`dueFrom` / `dueTo` / `statusCategory` / `approvalStatus`). On
  // mount we expand any alias into its canonical form, then strip the
  // alias from the URL so the user sees clean shareable state.
  //
  // The `useRef` guard is mandatory: in React 18 StrictMode the effect
  // would otherwise re-fire after we rewrite the URL and re-apply the
  // alias on top of the user's own subsequent edits.
  const aliasHandledRef = useRef(false)
  useEffect(() => {
    if (aliasHandledRef.current) return
    const filterAlias = searchParams.get('filter')
    const dueAlias = searchParams.get('due')
    if (!filterAlias && !dueAlias) return
    aliasHandledRef.current = true

    const next = new URLSearchParams(searchParams)
    next.delete('filter')
    next.delete('due')

    // Today / overdue helpers — bounded ISO `YYYY-MM-DD` strings. The
    // filter compares them against the date part of the backend's
    // `dueDate` (a full ISO datetime), sliced to `YYYY-MM-DD` at compare
    // time — see the `dueDay` slice in the row filter below.
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const toIsoDate = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const todayIso = toIsoDate(today)

    // ── Due-today shortcut: ?due=today  or  ?filter=due-today
    //  Maps to dueFrom = dueTo = today so the existing date-range
    //  filter does the work.
    if (
      dueAlias === 'today' ||
      filterAlias === 'due-today' ||
      filterAlias === 'today'
    ) {
      next.set('dueFrom', todayIso)
      next.set('dueTo', todayIso)
    }

    // ── Overdue shortcut: ?filter=overdue
    //  Time-aware `overdue` flag (not a date range) so a task due EARLIER
    //  TODAY with a specific time counts as overdue — matching the dashboard
    //  counts and the overdue notification. The row filter applies the
    //  canonical predicate (see below). Exclude done/cancelled so only OPEN
    //  overdue work surfaces.
    if (filterAlias === 'overdue') {
      next.set('overdue', '1')
      next.set('statusCategoryExclude', 'done,cancelled')
    }

    // ── Completed shortcut: ?filter=complete (or ?filter=completed)
    //  Filter by workflow category rather than status NAME — different
    //  workspaces use different status labels for "done", but the
    //  category enum is stable.
    if (filterAlias === 'complete' || filterAlias === 'completed') {
      next.set('statusCategory', 'done')
    }

    // ── Completed-this-week shortcut: ?filter=complete-week
    //  Deep-linked from the Company Admin dashboard's "Completed This
    //  Week" card. Sets a dedicated flag; the row filter then keeps only
    //  DELIVERED (done/closed) tasks completed on/after this week's start,
    //  reproducing the card's count so the admin can verify the number.
    if (filterAlias === 'complete-week') {
      next.set('completedWeek', '1')
    }

    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Live tenant-wide task list. Returns up to 500 hydrated rows; client
  // does the filtering on top so the chip popovers stay snappy.
  const tasksQuery = useQuery({
    queryKey: ['admin', 'tasks', 'monitor'],
    queryFn: async () => {
      const res = await api.get('/admin/tasks')
      // Keep the whole envelope — `statusCounts` (exact per-status totals)
      // and `truncated` ride alongside the (capped) task list.
      return res.data ?? { data: [] }
    },
    // Overdue is time-dependent (a task flips the instant its due time passes)
    // and due dates may be edited elsewhere — poll once a minute so the
    // overdue view stays live. Pauses while the tab is hidden.
    refetchInterval: 60_000,
  })
  // Memoised so the many `useMemo`s derived from it (filtered / groups /
  // taskById …) keep a stable dependency and don't recompute every render.
  const allTasks = useMemo(
    () => tasksQuery.data?.data ?? [],
    [tasksQuery.data],
  )
  // Exact per-status totals from the server — the group headers use these
  // so a capped list can't under-report (e.g. "TO DO: 302", not "74").
  const statusCounts = tasksQuery.data?.statusCounts ?? {}
  const listTruncated = tasksQuery.data?.truncated ?? false

  /** Admin-side task patch used by the Gantt drag-to-reschedule, date
   *  popover, and colour picker. Hits `PATCH /admin/tasks/:id` which
   *  bypasses the per-workspace membership gate (Company Admins are
   *  tenant-scoped by definition). The cache is updated optimistically
   *  so bars track the cursor without waiting for the round-trip;
   *  rollback on failure. */
  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, patch }) => {
      const res = await api.patch(`/admin/tasks/${id}`, patch)
      return res.data
    },
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['admin', 'tasks', 'monitor'] })
      const prev = qc.getQueryData(['admin', 'tasks', 'monitor'])
      qc.setQueryData(['admin', 'tasks', 'monitor'], (old) => {
        if (!old || !Array.isArray(old.data)) return old
        return {
          ...old,
          data: old.data.map((t) => (t.id === id ? { ...t, ...patch } : t)),
        }
      })
      return { prev }
    },
    onError: (err, _vars, context) => {
      if (context?.prev !== undefined) {
        qc.setQueryData(['admin', 'tasks', 'monitor'], context.prev)
      }
      toast.error(extractApiError(err).message)
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'tasks', 'monitor'] })
      // A status change can award/reverse points — refresh the points surfaces
      // so the Leaderboard / Performance update without a manual reload.
      if (vars?.patch?.status !== undefined) invalidateGamification(qc)
    },
  })

  const onUpdateTask = useCallback(
    (id, patch) => updateTaskMutation.mutate({ id, patch }),
    [updateTaskMutation],
  )

  const departments = useMemo(
    () =>
      Array.from(
        new Set(
          allTasks
            .map((t) => t.department)
            .filter((d) => typeof d === 'string' && d.length > 0),
        ),
      ).sort(),
    [allTasks],
  )
  // EVERY assignee on a row, not just the legacy first-assignee
  // `assigneeName` — a member who is second on a multi-assignee task
  // was otherwise unfilterable, and the Members page drill-down (which
  // counts per assignee id) landed on a blank list (QA 2026-07-22).
  const assignees = useMemo(
    () =>
      Array.from(
        new Set(
          allTasks
            .flatMap((t) =>
              Array.isArray(t.assignees) && t.assignees.length > 0
                ? t.assignees.map((a) => a?.name)
                : [t.assigneeName],
            )
            .filter((a) => typeof a === 'string' && a.length > 0),
        ),
      ).sort(),
    [allTasks],
  )

  // Read current filter values out of the URL.
  const departmentSel = readSet('department')
  const assigneeSel = readSet('assignee')
  const statusSel = readSet('status')
  const prioritySel = readSet('priority')
  // Recurring/repeat filter — values 'active' | 'paused' (a task's recurrence
  // state). The Repeat Task Summary email links here with ?recurring=active,paused.
  const recurringSel = readSet('recurring')
  // Verification/checker filter — values are `approvalStatus` keys
  // ('approved' | 'pending' | 'rejected' | 'none'); see VERIFICATION_META.
  // URL key is `approvalStatus` (the canonical shape this page documents and
  // the "task approved/rejected" notifications already deep-link to).
  const verificationSel = readSet('approvalStatus')
  const dueRange = {
    from: searchParams.get('dueFrom') || undefined,
    to: searchParams.get('dueTo') || undefined,
  }
  // Workflow-category include / exclude filters. These are the canonical
  // way to filter by lifecycle bucket (`backlog/active/review/done/
  // cancelled`) — different workspaces use different status NAMES so
  // category-level filtering is the only stable cross-workspace shape.
  const statusCategorySel = readSet('statusCategory')
  const statusCategoryExcludeSel = readSet('statusCategoryExclude')

  // "Completed this week" filter — deep-linked from the dashboard card so
  // its count can be verified. Reproduces the backend query: delivered
  // (done/closed) tasks whose `completedAt` is on/after the start of this
  // week. Week start = 00:00 on the COMPANY's first day of the week
  // (`workingWeekStartDay` — Monday unless the working week wraps over
  // Sunday) in the company timezone, which matches the server's
  // `zonedWeekStartUtc(…, workingWeekStartDay(dailyActiveDays))`, so the
  // same tasks are selected on both sides.
  const completedWeek = searchParams.get('completedWeek') === '1'
  const { schedule: reportSchedule } = useReportSchedule()
  const weekStartDow = workingWeekStartDay(reportSchedule?.dailyActiveDays)
  const weekStartMs = useMemo(() => {
    const now = DateTime.now().setZone(getCalendarTimeZone() || undefined)
    // `weekday` is the ISO weekday (Mon=1 … Sun=7); `% 7` maps it to JS
    // getDay() (Sun=0) to compare with the company start day.
    const back = ((now.weekday % 7) - weekStartDow + 7) % 7
    return now.minus({ days: back }).startOf('day').toMillis()
  }, [weekStartDow])

  // Time-aware overdue preset (?filter=overdue). `nowMs` = the exact instant
  // (for timed due dates); `todayIso` = tenant-tz start-of-today (for date-only
  // due dates, overdue only once the whole day has passed). Mirrors the backend
  // rule in flowdesk-api/src/utils/task-overdue.js.
  const overdueOnly = searchParams.get('overdue') === '1'
  // `overdueCtx` captures the current instant, so it must re-evaluate as the
  // clock advances — otherwise the overdue boundary froze at mount and a task
  // that passed its due time never entered the overdue view. Tick once a
  // minute (no network — just a fresh clock read).
  const [nowTick, setNowTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 60_000)
    return () => clearInterval(id)
  }, [])
  const overdueCtx = useMemo(() => {
    const now = DateTime.now().setZone(getCalendarTimeZone() || undefined)
    return { nowMs: now.toMillis(), todayIso: now.toFormat('yyyy-MM-dd') }
    // `nowTick` intentionally drives the recompute — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTick])

  // "Activity" scope — mirrors the Analytics dashboard's Today filter, but for
  // an arbitrary date window (presets Today / Yesterday / This week / This
  // month, or a custom range). A task counts when it was CREATED inside the
  // window, OR is RUNNING during it (its [startDate, dueDate] span overlaps the
  // window; a task with a start but no due date runs open-endedly). Day bounds
  // are the tenant DISPLAY timezone, matching the backend's tenant-tz day
  // (utils/timezone.zonedStartOfDayUtc) used by the analytics service. Bounds
  // are `YYYY-MM-DD`; either may be omitted for an open-ended window.
  const actRange = {
    from: searchParams.get('actFrom') || undefined,
    to: searchParams.get('actTo') || undefined,
  }
  const actWindow = useMemo(() => {
    if (!actRange.from && !actRange.to) return null
    const zone = getCalendarTimeZone() || undefined
    const lower = actRange.from ? DateTime.fromISO(actRange.from, { zone }) : null
    const upper = actRange.to ? DateTime.fromISO(actRange.to, { zone }) : null
    return {
      lowerMs: lower && lower.isValid ? lower.startOf('day').toMillis() : -Infinity,
      // Inclusive of the whole `to` day → start of the following day.
      upperMs:
        upper && upper.isValid
          ? upper.startOf('day').plus({ days: 1 }).toMillis()
          : Infinity,
    }
  }, [actRange.from, actRange.to])

  // Status filter chips show the actual workflow state names users see
  // in the picker (e.g. "Open" / "In Progress"), not the legacy bucket
  // keys. Filtering is by `statusName` for that reason.
  const statusNameOptions = useMemo(() => {
    const seen = new Map()
    for (const t of allTasks) {
      if (t.statusName && !seen.has(t.statusName)) {
        seen.set(t.statusName, t.statusColor ?? '#94a3b8')
      }
    }
    return Array.from(seen, ([name, color]) => ({ name, color }))
  }, [allTasks])

  // Free-text task-name search — applied alongside the structured
  // chip filters. Local state (not URL-synced) because admins typically
  // use it for transient lookups, not deep-linked shares; the chip
  // filters stay in the URL for the shareable cases.
  const [search, setSearch] = useState('')

  const filtered = useMemo(
    () =>
      allTasks.filter((t) => {
        if (departmentSel.size > 0 && !departmentSel.has(t.department)) return false
        if (assigneeSel.size > 0) {
          // Match ANY assignee on the row — the legacy `assigneeName`
          // is only the FIRST assignee, so a member further down the
          // stack never matched and the drill-down from the Members
          // page count showed a blank list (QA 2026-07-22).
          const names =
            Array.isArray(t.assignees) && t.assignees.length > 0
              ? t.assignees.map((a) => a?.name).filter(Boolean)
              : t.assigneeName
                ? [t.assigneeName]
                : []
          if (!names.some((n) => assigneeSel.has(n))) return false
        }
        if (statusSel.size > 0 && !statusSel.has(t.statusName)) return false
        if (prioritySel.size > 0 && !prioritySel.has(t.priority)) return false
        // Repeat filter: keep only tasks whose recurrence state is selected.
        // Non-recurring tasks (recurring === null) are excluded whenever any
        // repeat value is chosen.
        if (recurringSel.size > 0 && !recurringSel.has(t.recurring)) return false
        // Verification/checker state — match on the task's approval status
        // (defaulting to 'none' for rows that never entered verification).
        if (
          verificationSel.size > 0 &&
          !verificationSel.has(t.approvalStatus ?? 'none')
        )
          return false
        if (
          statusCategorySel.size > 0 &&
          !statusCategorySel.has(t.statusCategory)
        )
          return false
        if (
          statusCategoryExcludeSel.size > 0 &&
          statusCategoryExcludeSel.has(t.statusCategory)
        )
          return false
        // Any active due-date filter implies "tasks that have a due
        // date inside this range" — undated tasks never satisfy that,
        // so they're excluded as soon as either bound is set. Earlier
        // the `&& t.dueDate` short-circuit let undated tasks slip
        // through every comparison and appear in "Due this week".
        if (dueRange.from || dueRange.to) {
          if (!t.dueDate) return false
          // `dueRange` bounds are date-only (`YYYY-MM-DD`); `t.dueDate` is
          // now a full ISO datetime, so compare on the date part only.
          // Comparing the whole ISO string would push a task due on the
          // `to` day (e.g. "2026-07-18T09:30Z") past "2026-07-18" and
          // wrongly drop it from the range.
          const dueDay = t.dueDate.slice(0, 10)
          if (dueRange.from && dueDay < dueRange.from) return false
          if (dueRange.to && dueDay > dueRange.to) return false
        }
        if (overdueOnly) {
          // Mirror the backend's overdue definition EXACTLY (Dashboard's
          // `overdueTasksCount` = `completedAt: null` + past-due). A task that
          // carries a `completedAt` stamp is closed (every terminal category —
          // done / cancelled / closed — stamps it) and is never "overdue",
          // regardless of its status CATEGORY. Relying on `statusCategoryExclude`
          // alone let a COMPLETED task whose workflow state isn't the
          // last-active-by-order state (so its stored category is 'active', not
          // 'done') leak into this view — making Task Monitor read one higher
          // than the Dashboard card. Guarding on `completedAt` keeps both counts
          // identical. See admin.service.js `overdueTasksCount`.
          if (t.completedAt) return false
          if (!t.dueDate) return false
          // Timed → past the instant; date-only → the whole day has passed,
          // compared by CALENDAR DAY in the tenant display timezone (NOT the
          // raw UTC date). Using `dueDate.slice(0,10)` compared a UTC date
          // string, so a date-only task due TODAY in a positive-offset tz
          // (stored at local-midnight = previous UTC day) read as "yesterday"
          // and wrongly counted as overdue — one higher than the backend, which
          // compares `dueDate < tenant-tz start-of-today`. `dayOffsetFromToday`
          // is the same tenant-tz calendar-day rule team-stats.js + the backend
          // (utils/task-overdue.js) use, so all three now agree.
          const pastDue = t.dueDateHasTime
            ? new Date(t.dueDate).getTime() < overdueCtx.nowMs
            : dayOffsetFromToday(t.dueDate) < 0
          if (!pastDue) return false
        }
        if (completedWeek) {
          // Delivered work only (done/closed) — excludes cancelled tasks,
          // which also carry a `completedAt` stamp. Then require the
          // completion to fall on/after this week's start. Mirrors the
          // dashboard "Completed This Week" count exactly.
          if (t.statusCategory !== 'done' && t.statusCategory !== 'closed')
            return false
          if (!t.completedAt) return false
          if (new Date(t.completedAt).getTime() < weekStartMs) return false
        }
        if (actWindow) {
          // Created inside the window …
          const createdMs = t.createdAt ? new Date(t.createdAt).getTime() : NaN
          const createdIn =
            Number.isFinite(createdMs) &&
            createdMs >= actWindow.lowerMs &&
            createdMs < actWindow.upperMs
          // … OR running during it: it has started before the window ends and
          // hasn't already ended before the window starts (dueDate ≥ window
          // start, or no due date at all). Same predicate as the analytics
          // service's `todayTaskMatch`, generalised to an arbitrary window.
          const startMs = t.startDate ? new Date(t.startDate).getTime() : NaN
          const dueMs = t.dueDate ? new Date(t.dueDate).getTime() : NaN
          const runningIn =
            Number.isFinite(startMs) &&
            startMs < actWindow.upperMs &&
            (!t.dueDate || (Number.isFinite(dueMs) && dueMs >= actWindow.lowerMs))
          if (!createdIn && !runningIn) return false
        }
        if (search.trim()) {
          const q = search.trim().toLowerCase()
          // Space / List come through as nested `{ id, name }` objects (as
          // used everywhere else on this page), NOT flat `spaceName` /
          // `listName` — reading the flat keys matched only the task name, so
          // "search by Space or List" (per the placeholder) silently returned
          // nothing.
          const hay =
            `${t.name ?? ''} ${t.space?.name ?? ''} ${t.list?.name ?? ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      }),
    [
      allTasks,
      departmentSel,
      assigneeSel,
      statusSel,
      prioritySel,
      recurringSel,
      verificationSel,
      statusCategorySel,
      statusCategoryExcludeSel,
      dueRange.from,
      dueRange.to,
      completedWeek,
      weekStartMs,
      overdueOnly,
      overdueCtx,
      actWindow,
      search,
    ],
  )

  // Active-filter pills shown below the chip row. Each entry knows how to
  // remove itself when the user clicks the X on its pill.
  const activeFilters = []
  for (const v of departmentSel)
    activeFilters.push({
      key: `dept:${v}`,
      label: `Department: ${v}`,
      onRemove: () => {
        const next = new Set(departmentSel)
        next.delete(v)
        writeSet('department', next)
      },
    })
  for (const v of assigneeSel)
    activeFilters.push({
      key: `assignee:${v}`,
      label: `Assignee: ${v}`,
      onRemove: () => {
        const next = new Set(assigneeSel)
        next.delete(v)
        writeSet('assignee', next)
      },
    })
  for (const v of statusSel)
    activeFilters.push({
      key: `status:${v}`,
      label: `Status: ${v}`,
      onRemove: () => {
        const next = new Set(statusSel)
        next.delete(v)
        writeSet('status', next)
      },
    })
  for (const v of prioritySel)
    activeFilters.push({
      key: `priority:${v}`,
      label: `Priority: ${PRIORITY_META[v]?.label ?? v}`,
      onRemove: () => {
        const next = new Set(prioritySel)
        next.delete(v)
        writeSet('priority', next)
      },
    })
  for (const v of recurringSel)
    activeFilters.push({
      key: `recurring:${v}`,
      label: `Repeat: ${v === 'active' ? 'Recurring (active)' : v === 'paused' ? 'Recurring (paused)' : v}`,
      onRemove: () => {
        const next = new Set(recurringSel)
        next.delete(v)
        writeSet('recurring', next)
      },
    })
  for (const v of verificationSel)
    activeFilters.push({
      key: `approvalStatus:${v}`,
      label: `Verification: ${VERIFICATION_META[v]?.label ?? v}`,
      onRemove: () => {
        const next = new Set(verificationSel)
        next.delete(v)
        writeSet('approvalStatus', next)
      },
    })
  if (dueRange.from || dueRange.to) {
    const range = dueRange.from && dueRange.to
      ? `${dueRange.from} → ${dueRange.to}`
      : dueRange.from
        ? `from ${dueRange.from}`
        : `until ${dueRange.to}`
    activeFilters.push({
      key: 'due',
      label: `Due: ${range}`,
      onRemove: () => {
        const next = new URLSearchParams(searchParams)
        next.delete('dueFrom')
        next.delete('dueTo')
        setSearchParams(next, { replace: true })
      },
    })
  }
  if (completedWeek) {
    activeFilters.push({
      key: 'completedWeek',
      label: 'Completed this week',
      onRemove: () => {
        const next = new URLSearchParams(searchParams)
        next.delete('completedWeek')
        setSearchParams(next, { replace: true })
      },
    })
  }
  if (overdueOnly) {
    activeFilters.push({
      key: 'overdue',
      label: 'Overdue',
      onRemove: () => {
        const next = new URLSearchParams(searchParams)
        next.delete('overdue')
        setSearchParams(next, { replace: true })
      },
    })
  }
  if (actRange.from || actRange.to) {
    const range =
      actRange.from && actRange.to
        ? actRange.from === actRange.to
          ? actRange.from
          : `${actRange.from} → ${actRange.to}`
        : actRange.from
          ? `from ${actRange.from}`
          : `until ${actRange.to}`
    activeFilters.push({
      key: 'activity',
      label: `Activity: ${range}`,
      onRemove: () => {
        const next = new URLSearchParams(searchParams)
        next.delete('actFrom')
        next.delete('actTo')
        setSearchParams(next, { replace: true })
      },
    })
  }
  for (const v of statusCategorySel) {
    activeFilters.push({
      key: `statusCat:${v}`,
      label: `Category: ${v}`,
      onRemove: () => {
        const next = new Set(statusCategorySel)
        next.delete(v)
        writeSet('statusCategory', next)
      },
    })
  }
  for (const v of statusCategoryExcludeSel) {
    activeFilters.push({
      key: `statusCatExcl:${v}`,
      label: `Excl: ${v}`,
      onRemove: () => {
        const next = new Set(statusCategoryExcludeSel)
        next.delete(v)
        writeSet('statusCategoryExclude', next)
      },
    })
  }

  function clearAllFilters() {
    const next = new URLSearchParams(searchParams)
    for (const k of [
      'department',
      'assignee',
      'status',
      'priority',
      'recurring',
      'approvalStatus',
      'dueFrom',
      'dueTo',
      'statusCategory',
      'statusCategoryExclude',
      'completedWeek',
      'overdue',
      'actFrom',
      'actTo',
    ]) {
      next.delete(k)
    }
    setSearchParams(next, { replace: true })
  }

  /** Group tasks by the actual status NAME (TO DO / IN PROGRESS /
   *  COMPLETE / …) rather than by workflow category. The picker
   *  dropdown was already flattened to a per-status list — both List
   *  and Board mirror that, so admins see one section / column per
   *  status instead of an Active/Done parent grouping.
   *
   *  Ordering follows the workflow's authored `order` (the sequence the
   *  admin arranged in the Workflows editor), matching the Space
   *  List/Board. Category then name are tie-breaks only. */
  // Subtask grouping — same pattern as the Spaces / Team Tasks pages.
  // The backend now returns subtasks alongside parents (with their
  // `parentTaskId` set); we group children under their parent here
  // so the table can render an expand chevron per parent.
  const subtasksByParent = useMemo(() => {
    const map = new Map()
    for (const t of filtered) {
      if (!t.parentTaskId) continue
      const key = String(t.parentTaskId)
      const arr = map.get(key) ?? []
      arr.push(t)
      map.set(key, arr)
    }
    return map
  }, [filtered])

  const statusGroups = useMemo(() => {
    const byName = new Map()
    const presentIds = new Set(filtered.map((t) => String(t.id)))
    for (const t of filtered) {
      // Only top-level tasks contribute to status groups — subtasks
      // render nested under their parent, not in the main sort.
      // Exception: a matching subtask whose parent ISN'T in the filtered
      // set would otherwise vanish (its parent row never renders), leaving
      // the visible list SHORT of the group's subtask-inclusive count
      // (`statusTotals` / server `statusCounts`) — e.g. header "COMPLETE 15"
      // over only a handful of visible rows. Surface those orphans as their
      // own top-level rows so the number always reconciles ("subtasks
      // included"). With no filter active the parent is always in view, so
      // this only fires once a filter/search has excluded the parent.
      if (t.parentTaskId) {
        const parentInView = presentIds.has(String(t.parentTaskId))
        if (parentInView) continue
      }
      const key = t.statusName || '—'
      if (!byName.has(key)) {
        byName.set(key, {
          name: key,
          color: t.statusColor ?? '#94a3b8',
          category: t.statusCategory ?? 'backlog',
          // Prefer the workflow's authored order (TO DO=0, IN PROGRESS=1,
          // COMPLETE=2, etc.) so the columns / list sections appear in
          // the same sequence as the Workflows page. Without this, two
          // statuses sharing a category (e.g. TO DO + IN PROGRESS both
          // `active`) flip to alphabetical order, putting "IN
          // PROGRESS" before "TO DO".
          order: typeof t.statusOrder === 'number' ? t.statusOrder : 999,
          tasks: [],
        })
      }
      byName.get(key).tasks.push(t)
    }
    return [...byName.values()].sort((a, b) => {
      // Primary: the workflow's authored order — the exact sequence the
      // admin arranged in the Workflows editor — so the Task Monitor
      // matches the Space List/Board (which render statuses in `order`).
      // Category and name are tie-breaks only (mixed workflows across
      // departments, or rows with no resolved order).
      if (a.order !== b.order) return a.order - b.order
      const ai = STATUS_CATEGORY_ORDER.indexOf(a.category)
      const bi = STATUS_CATEGORY_ORDER.indexOf(b.category)
      if (ai !== bi) return ai - bi
      return a.name.localeCompare(b.name)
    })
  }, [filtered])

  // ── Bulk edit (#6) ──────────────────────────────────────────────────
  // Selection is scoped to the List view's TOP-LEVEL rows, in render order,
  // so a Shift+click range can span status sections. Subtasks aren't
  // individually selectable here — the parent row is the unit of bulk action.
  // Collapsed sections are excluded: their rows aren't rendered, so a
  // Shift+click range must not sweep them in (and collapsing a section drops
  // its rows from the selection via the prune below — never act off-screen).
  const orderedIds = useMemo(
    () =>
      statusGroups
        .filter((g) => !collapsed.has(g.name))
        .flatMap((g) => g.tasks.map((t) => String(t.id))),
    [statusGroups, collapsed],
  )
  const selection = useRangeSelection(orderedIds)
  const { selected: selectedSet } = selection
  // Prune any selected id that has left the current view (filter / search
  // change, or a refetch) so the bulk bar never acts on rows off-screen.
  useEffect(() => {
    selection.setSelected((prev) => {
      const present = new Set(orderedIds)
      let changed = false
      const next = new Set()
      for (const id of prev) {
        if (present.has(id)) next.add(id)
        else changed = true
      }
      return changed ? next : prev
    })
    // selection.setSelected is a stable useState setter; re-run on view change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedIds])

  const taskById = useMemo(() => {
    const m = new Map()
    for (const t of allTasks) m.set(String(t.id), t)
    return m
  }, [allTasks])
  const selectedIds = useMemo(
    () => [...selectedSet].filter((id) => taskById.has(id)),
    [selectedSet, taskById],
  )
  const selectedTasks = useMemo(
    () => selectedIds.map((id) => taskById.get(id)).filter(Boolean),
    [selectedIds, taskById],
  )

  // Per-status totals counting EVERY matching task by its own status —
  // subtasks included, regardless of where they render (nested under a
  // parent). Drives the status-group count badge when filters are
  // active, so the number reconciles with the server's `statusCounts`
  // (which also counts subtasks) and with the Gantt view. Without this
  // the badge showed only top-level parents and undercounted.
  const statusTotals = useMemo(() => {
    const map = new Map()
    for (const t of filtered) {
      const key = t.statusName || '—'
      map.set(key, (map.get(key) ?? 0) + 1)
    }
    return map
  }, [filtered])

  // Single source of truth for a status group's headline count — shared by
  // the List sections AND the Board columns so the two views can never
  // disagree (the reported bug: List showed the subtask-inclusive server
  // total, e.g. TO DO 118, while the Board showed only the top-level parent
  // cards, e.g. TO DO 36).
  //
  // Only when NOTHING is narrowing the set (no chip filters AND no search)
  // do we use the server's exact per-status total (subtask-inclusive and
  // uncapped — so a truncated 5,000-row load still reports the true company
  // total). The instant any filter OR the free-text search is active we fall
  // back to `statusTotals`, the client tally over the actually-filtered set
  // (also subtask-inclusive), so the badge reflects exactly what's on screen.
  // `search` is intentionally NOT part of `activeFilters` (it's transient,
  // non-URL-synced), so it must be checked explicitly here. Never
  // `rows.length`, which counts only the parent cards and undercounts.
  const groupCount = (g) =>
    activeFilters.length === 0 && !search.trim()
      ? statusCounts[g.name] ?? statusTotals.get(g.name) ?? g.tasks.length
      : statusTotals.get(g.name) ?? g.tasks.length

  function toggleGroup(s) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  }

  return (
    <div>
      <Breadcrumb current="Task Monitor" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
          Task Monitor
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          See every task in your company.
        </p>
      </div>

      {/* Free-text search sits above the chip bar so the admin's
       *  most-likely action ("find this specific task") is one click
       *  + a few keystrokes away — the chips below handle bulk
       *  filtering by attribute. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search by Task Name, Space or List"
        />
      </FilterRow>

      {/* Filter bar — chip-based, multi-select, URL-synced. */}
      <FilterBar
        activeFilters={activeFilters}
        onClearAll={activeFilters.length > 0 ? clearAllFilters : undefined}
        // Export what the user is LOOKING at — the filtered set. It
        // used to export `allTasks` (the whole unfiltered feed, every
        // status + subtasks), so its count disagreed with both the
        // visible list and the Open Tasks tile (QA-reported).
        trailing={<CsvExportButton tasks={filtered} />}
      >
        {/* "Activity" scope — tasks CREATED or RUNNING inside the selected
         *  window (presets Today / Yesterday / This week / This month, or a
         *  custom range), mirroring the Analytics dashboard's Today filter.
         *  Distinct from the "Due" chip, which filters by due date only. */}
        <DateRangeChip
          label="Activity"
          icon={CalendarClock}
          value={actRange}
          onChange={(next) => {
            // Atomic write — both bounds in one update (see `writeStrs`).
            writeStrs({ actFrom: next.from ?? '', actTo: next.to ?? '' })
          }}
        />
        <FilterChip
          label="Department"
          icon={Building2}
          options={departments.map((d) => ({ value: d, label: d }))}
          selected={departmentSel}
          onChange={(next) => writeSet('department', next)}
          searchPlaceholder="Search Departments…"
        />
        <FilterChip
          label="Assignee"
          icon={User}
          options={assignees.map((a) => ({ value: a, label: a }))}
          selected={assigneeSel}
          onChange={(next) => writeSet('assignee', next)}
          searchPlaceholder="Search People…"
        />
        <FilterChip
          label="Status"
          icon={CircleDashed}
          options={statusNameOptions.map((s) => ({
            value: s.name,
            label: s.name,
            swatch: s.color,
          }))}
          selected={statusSel}
          onChange={(next) => writeSet('status', next)}
          searchPlaceholder="Search Statuses…"
        />
        <FilterChip
          label="Priority"
          icon={Flag}
          options={['urgent', 'high', 'normal', 'low'].map((p) => ({
            value: p,
            label: PRIORITY_META[p].label,
            swatch: PRIORITY_META[p].color,
          }))}
          selected={prioritySel}
          onChange={(next) => writeSet('priority', next)}
          searchPlaceholder="Search Priorities…"
        />
        <FilterChip
          label="Repeat"
          icon={Repeat}
          options={[
            { value: 'active', label: 'Recurring (active)', swatch: '#16a34a' },
            { value: 'paused', label: 'Recurring (paused)', swatch: '#94a3b8' },
          ]}
          selected={recurringSel}
          onChange={(next) => writeSet('recurring', next)}
        />
        <FilterChip
          label="Verification"
          icon={ShieldCheck}
          options={VERIFICATION_ORDER.map((v) => ({
            value: v,
            label: VERIFICATION_META[v].label,
            swatch: VERIFICATION_META[v].swatch,
          }))}
          selected={verificationSel}
          onChange={(next) => writeSet('approvalStatus', next)}
          searchPlaceholder="Search verification…"
        />
        <DateRangeChip
          label="Due"
          icon={CalendarDays}
          value={dueRange}
          onChange={(next) => {
            // Atomic write — both bounds in one update. Two separate
            // `writeStr` calls would clobber each other (see `writeStrs`),
            // which is why the "From" date never stuck.
            writeStrs({ dueFrom: next.from ?? '', dueTo: next.to ?? '' })
          }}
        />
      </FilterBar>

      {/* View tabs */}
      <div className="mb-4 flex items-center gap-2">
        <ViewTab
          label="List"
          icon={<LayoutList size={14} />}
          active={view === 'list'}
          onClick={() => setView('list')}
        />
        <ViewTab
          label="Board"
          icon={<LayoutGrid size={14} />}
          active={view === 'board'}
          onClick={() => setView('board')}
        />
        <ViewTab
          label="Gantt"
          icon={<GanttChart size={14} />}
          active={view === 'gantt'}
          onClick={() => setView('gantt')}
        />
      </div>

      {/* Loading state — first paint while the tenant-wide query is in flight. */}
      {tasksQuery.isLoading ? (
        <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] px-6 py-10 text-center">
          <p className="text-sm text-[color:var(--color-ink-muted)]">
            Loading tasks…
          </p>
        </div>
      ) : null}

      {/* Error state — the tenant-wide query rejected. Surface this
       *  BEFORE the empty state so an API failure isn't silently rendered
       *  as "No Tasks Yet". Includes a Retry shortcut so the admin can
       *  re-run without a full page refresh. */}
      {!tasksQuery.isLoading && tasksQuery.isError ? (
        <div className="rounded-xl border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-priority-urgent)]/5 px-6 py-10 text-center">
          <h2 className="text-lg font-medium text-[color:var(--color-priority-urgent)] mb-1">
            Couldn&rsquo;t Load Tasks
          </h2>
          <p className="text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto mb-3">
            {extractApiError(tasksQuery.error).message}
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => tasksQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      {/* Hero empty state — the tenant has no tasks at all. Shown instead
       *  of four identical "No tasks match" rows. Disappears as soon as any
       *  Manager / Team Leader creates a task in any Space. Gated on
       *  `!isError` so a failed query never triggers this branch. */}
      {!tasksQuery.isLoading && !tasksQuery.isError && allTasks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] px-6 py-12 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] mb-3">
            <LayoutList size={20} />
          </div>
          <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0 mb-1">
            No Tasks Yet
          </h2>
          <p className="text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto">
            Tasks created in any Space across this company will appear
            here. Ask a Manager or Team Leader to upload an SOP and approve
            its task breakdown to get started.
          </p>
        </div>
      ) : null}

      {/* Filtered-to-empty state — there ARE tasks in the tenant, but the
       *  current chip selection excludes all of them. Surfaces a "Clear all"
       *  shortcut so the user isn't stuck with an empty page. */}
      {!tasksQuery.isLoading &&
      allTasks.length > 0 &&
      filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] px-6 py-10 text-center">
          <p className="text-sm text-[color:var(--color-ink-muted)] mb-3">
            No tasks match the current filters.
          </p>
          {activeFilters.length > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={clearAllFilters}
            >
              Clear All
            </Button>
          ) : null}
        </div>
      ) : null}

      {!tasksQuery.isLoading && filtered.length > 0 ? (
      <>
      {listTruncated ? (
        <div className="mb-4 rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] px-3 py-2 text-sm text-[color:var(--color-warning)]">
          Showing the 5,000 most recent tasks — the status-group counts are
          exact totals for the whole company, so they may be higher than the
          rows listed here.
        </div>
      ) : null}

      {view === 'list' ? (
        <>
        {selectedIds.length > 0 ? (
          <TaskMonitorBulkBar
            selectedIds={selectedIds}
            selectedTasks={selectedTasks}
            allTasks={allTasks}
            onClear={selection.clear}
          />
        ) : null}
        <div className="space-y-4" ref={listScrollRef}>
          {statusGroups.map((g) => {
            const rows = g.tasks
            const isCollapsed = collapsed.has(g.name)
            const sectionIds = rows.map((t) => String(t.id))
            const allSelected =
              sectionIds.length > 0 &&
              sectionIds.every((id) => selection.isSelected(id))
            const someSelected = sectionIds.some((id) => selection.isSelected(id))
            const toggleSection = () =>
              selection.setSelected((prev) => {
                const next = new Set(prev)
                if (allSelected) sectionIds.forEach((id) => next.delete(id))
                else sectionIds.forEach((id) => next.add(id))
                return next
              })
            return (
              <section
                key={g.name}
                className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(g.name)}
                  className="w-full flex items-center gap-2 px-4 py-2.5 bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] text-left cursor-pointer hover:bg-[color:var(--color-card-hover)]"
                  aria-expanded={!isCollapsed}
                >
                  {isCollapsed ? (
                    <ChevronRight size={14} className="text-[color:var(--color-ink-muted)]" />
                  ) : (
                    <ChevronDown size={14} className="text-[color:var(--color-ink-muted)]" />
                  )}
                  <StatusPill name={g.name} color={g.color} />
                  <span className="text-sm text-[color:var(--color-ink-muted)]">
                    {/* Shared `groupCount` — the server's exact per-status
                     *  total when unfiltered, the filtered client tally
                     *  otherwise. Both count subtasks by their own status,
                     *  so the badge matches the KPI cards, the Gantt view
                     *  and the Board columns (same source of truth). */}
                    ({groupCount(g)})
                  </span>
                </button>

                {isCollapsed ? null : (
                  <div className="overflow-x-auto" data-fd-xscroll>
                    {/* `table-fixed` + explicit `<colgroup>` widths so
                     *  every status section (TO DO / IN PROGRESS /
                     *  COMPLETE / …) sizes its columns identically.
                     *  Without this each section runs auto layout
                     *  against its own content and the columns drift
                     *  between sections. Widths sum to 100%. The Name
                     *  column is intentionally the widest so long task
                     *  and (indented) subtask names aren't truncated. */}
                    {/* `min-w-[2040px]` gives the table a comfortable floor so
                     *  that when the container is narrower (sidebar expanded /
                     *  smaller screens) it overflows the `overflow-x-auto`
                     *  wrapper above and shows a horizontal scrollbar, instead
                     *  of squeezing every column. The floor was widened as
                     *  columns were added (Checker, then Start/Due Time) so
                     *  every column reads without truncation. On wide screens
                     *  `w-full` still fills the space. */}
                    <table className="w-full min-w-[2040px] table-fixed text-left text-sm">
                      <colgroup>
                        <col style={{ width: '40px' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '5%' }} />
                        <col style={{ width: '6%' }} />
                        <col style={{ width: '8%' }} />
                      </colgroup>
                      <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
                        <tr className="border-b-2 border-[color:var(--color-rule)]">
                          <th className="px-4 py-3">
                            <SelectCheckbox
                              checked={allSelected}
                              indeterminate={someSelected && !allSelected}
                              onClick={toggleSection}
                              ariaLabel={`Select all ${g.name} tasks`}
                            />
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Task Name
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Department
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Space
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Assignee
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Checker
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Start Date
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Start Time
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Due Date
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Due Time
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Priority
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Comments
                          </th>
                          <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                            Created By
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.flatMap((t) => {
                          const subs =
                            subtasksByParent.get(String(t.id)) ?? []
                          const hasSubs = subs.length > 0
                          const isExpanded = expandedParents.has(t.id)
                          // Top-level row + optional subtask rows
                          // rendered inline as siblings — `flatMap` is
                          // the cleanest way to interleave them
                          // without an extra wrapper element.
                          const parentRow = (
                            <tr
                              key={t.id}
                              className={
                                'border-b border-[color:var(--color-rule)] last:border-b-0 hover:bg-[color:var(--color-card-hover)] ' +
                                (selection.isSelected(String(t.id))
                                  ? 'bg-[color:var(--color-accent-bg)]'
                                  : '')
                              }
                            >
                              <td className="px-4 py-3 align-middle">
                                <SelectCheckbox
                                  checked={selection.isSelected(String(t.id))}
                                  onClick={(e) =>
                                    selection.handleSelectClick(e, String(t.id))
                                  }
                                  ariaLabel={`Select task ${t.name}`}
                                />
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink-strong)] font-normal">
                                <div className="flex items-center gap-2 min-w-0">
                                  {hasSubs ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleParent(t.id)}
                                      aria-expanded={isExpanded}
                                      title={
                                        isExpanded
                                          ? 'Collapse subtasks'
                                          : 'Expand subtasks'
                                      }
                                      className="p-0.5 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]"
                                    >
                                      <ChevronRight
                                        size={14}
                                        className={
                                          'shrink-0 transition-transform ' +
                                          (isExpanded ? 'rotate-90' : '')
                                        }
                                      />
                                    </button>
                                  ) : (
                                    // Spacer keeps task-name columns
                                    // aligned across rows with + without
                                    // subtasks.
                                    <span className="w-4 shrink-0" aria-hidden="true" />
                                  )}
                                  <StatusDot
                                    color={t.statusColor ?? '#94a3b8'}
                                    unstarted={isUnstartedStatus(
                                      { category: t.statusCategory },
                                      t.statusName ?? t.status,
                                    )}
                                    size={14}
                                  />
                                  {/* Click the name to jump to the task in
                                   *  its Space List and scroll to it — same
                                   *  ?focusTask deep link the Team Tasks page
                                   *  uses. Falls back to plain text if the
                                   *  space/list couldn't be resolved. */}
                                  {t.space?.id && t.list?.id ? (
                                    <Link
                                      to={`/app/spaces/${t.space.id}/lists/${t.list.id}?focusTask=${t.id}`}
                                      className="truncate hover:underline cursor-pointer"
                                      title="Open task in its list"
                                    >
                                      {t.name}
                                    </Link>
                                  ) : (
                                    <span className="truncate">{t.name}</span>
                                  )}
                                  {hasSubs ? (
                                    <span
                                      className="ml-1 text-[10px] text-[color:var(--color-ink-muted)] inline-flex items-center gap-0.5"
                                      title={`${subs.length} subtask${
                                        subs.length === 1 ? '' : 's'
                                      }`}
                                    >
                                      <GitBranch size={11} />
                                      {subs.length}
                                    </span>
                                  ) : null}
                                  {t.recurring ? (
                                    <span
                                      className="ml-1 inline-flex items-center"
                                      style={{
                                        color:
                                          t.recurring === 'active'
                                            ? '#16a34a'
                                            : 'var(--color-ink-muted)',
                                      }}
                                      title={
                                        t.recurring === 'active'
                                          ? 'Recurring (active)'
                                          : 'Recurring (paused)'
                                      }
                                      aria-label={
                                        t.recurring === 'active'
                                          ? 'Recurring task, active'
                                          : 'Recurring task, paused'
                                      }
                                    >
                                      <Repeat size={12} />
                                    </span>
                                  ) : null}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink)]">
                                {t.department ?? '—'}
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink)]">
                                {t.space?.name ?? '—'}
                              </td>
                              <td className="px-4 py-3">
                                {t.assignees && t.assignees.length > 0 ? (
                                  <AvatarStack people={t.assignees} size={24} />
                                ) : t.assigneeName ? (
                                  // Back-compat: a still-warm cache from
                                  // before the API was upgraded.
                                  <AvatarStack
                                    people={[{ name: t.assigneeName }]}
                                    size={24}
                                  />
                                ) : (
                                  <span className="text-[color:var(--color-ink-muted)]">
                                    Unassigned
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink)]">
                                {t.verifierName ?? (
                                  <span className="text-[color:var(--color-ink-muted)]">
                                    No checker
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink)] whitespace-nowrap">
                                {taskDay(t.startDate, t.startDateHasTime)}
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink)] whitespace-nowrap">
                                {taskTime(t.startDate, t.startDateHasTime) || '—'}
                              </td>
                              <td
                                className="px-4 py-3 font-medium whitespace-nowrap"
                                style={{ color: dueDateColor(t.dueDate, t.completedAt) }}
                              >
                                {taskDay(t.dueDate, t.dueDateHasTime)}
                              </td>
                              <td
                                className="px-4 py-3 font-medium whitespace-nowrap"
                                style={{ color: dueDateColor(t.dueDate, t.completedAt) }}
                              >
                                {taskTime(t.dueDate, t.dueDateHasTime) || '—'}
                              </td>
                              <td className="px-4 py-3">
                                <PriorityBadge priority={t.priority} />
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink)]">
                                {/* Shared comments popover — count
                                 *  + click-to-open thread, identical
                                 *  to the Space List view's cell.
                                 *  initialCount avoids a fetch per
                                 *  row on mount; the live count
                                 *  takes over once the popup opens
                                 *  and the full thread loads. */}
                                <CommentsCell
                                  taskId={t.id}
                                  currentUserId={currentUserId}
                                  initialCount={t.commentCount ?? 0}
                                  zeroLabel="—"
                                />
                              </td>
                              <td className="px-4 py-3 text-[color:var(--color-ink)]">
                                {t.createdByName ?? t.createdBy ?? '—'}
                              </td>
                            </tr>
                          )
                          // Subtask rows rendered when expanded.
                          // Indented inside the Name column so they nest
                          // clearly under the parent: `pl-12` lands the
                          // subtask status dot roughly under where the
                          // parent name text begins (parent leads with a
                          // chevron + status dot ≈ 48px), giving a clear
                          // one-level indent.
                          const subRows =
                            isExpanded
                              ? subs.map((sub) => (
                                  <tr
                                    key={sub.id}
                                    className="border-b border-[color:var(--color-rule)] last:border-b-0 bg-[color:var(--color-canvas)]/40 hover:bg-[color:var(--color-card-hover)]"
                                  >
                                    {/* Subtasks aren't individually bulk-selectable
                                     *  — spacer keeps the columns aligned with the
                                     *  parent rows' checkbox cell. */}
                                    <td className="px-4 py-3" aria-hidden="true" />
                                    <td className="px-4 py-3 text-[color:var(--color-ink-strong)]">
                                      <div className="flex items-center gap-2 min-w-0 pl-12">
                                        <StatusDot
                                          color={sub.statusColor ?? '#94a3b8'}
                                          unstarted={isUnstartedStatus(
                                            { category: sub.statusCategory },
                                            sub.statusName ?? sub.status,
                                          )}
                                          size={12}
                                        />
                                        {(sub.space?.id ?? t.space?.id) &&
                                        (sub.list?.id ?? t.list?.id) ? (
                                          <Link
                                            to={`/app/spaces/${sub.space?.id ?? t.space.id}/lists/${sub.list?.id ?? t.list.id}?focusTask=${sub.id}`}
                                            className="truncate text-[color:var(--color-ink)] hover:underline cursor-pointer"
                                            title="Open task in its list"
                                          >
                                            {sub.name}
                                          </Link>
                                        ) : (
                                          <span className="truncate text-[color:var(--color-ink)]">
                                            {sub.name}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-[color:var(--color-ink-muted)]">
                                      {sub.department ?? '—'}
                                    </td>
                                    <td className="px-4 py-3 text-[color:var(--color-ink-muted)]">
                                      {sub.space?.name ?? t.space?.name ?? '—'}
                                    </td>
                                    <td className="px-4 py-3">
                                      {sub.assignees && sub.assignees.length > 0 ? (
                                        <AvatarStack
                                          people={sub.assignees}
                                          size={22}
                                        />
                                      ) : sub.assigneeName ? (
                                        <AvatarStack
                                          people={[{ name: sub.assigneeName }]}
                                          size={22}
                                        />
                                      ) : (
                                        <span className="text-[color:var(--color-ink-muted)]">
                                          Unassigned
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-[color:var(--color-ink-muted)]">
                                      {sub.verifierName ?? '—'}
                                    </td>
                                    <td className="px-4 py-3 text-[color:var(--color-ink-muted)] whitespace-nowrap">
                                      {taskDay(sub.startDate, sub.startDateHasTime)}
                                    </td>
                                    <td className="px-4 py-3 text-[color:var(--color-ink-muted)] whitespace-nowrap">
                                      {taskTime(sub.startDate, sub.startDateHasTime) || '—'}
                                    </td>
                                    <td
                                      className="px-4 py-3 font-medium whitespace-nowrap"
                                      style={{ color: dueDateColor(sub.dueDate, sub.completedAt) }}
                                    >
                                      {taskDay(sub.dueDate, sub.dueDateHasTime)}
                                    </td>
                                    <td
                                      className="px-4 py-3 font-medium whitespace-nowrap"
                                      style={{ color: dueDateColor(sub.dueDate, sub.completedAt) }}
                                    >
                                      {taskTime(sub.dueDate, sub.dueDateHasTime) || '—'}
                                    </td>
                                    <td className="px-4 py-3">
                                      <PriorityBadge priority={sub.priority} />
                                    </td>
                                    <td className="px-4 py-3 text-[color:var(--color-ink-muted)]">
                                      <CommentsCell
                                        taskId={sub.id}
                                        currentUserId={currentUserId}
                                        initialCount={sub.commentCount ?? 0}
                                        zeroLabel="—"
                                      />
                                    </td>
                                    <td className="px-4 py-3 text-[color:var(--color-ink-muted)]">
                                      {sub.createdByName ?? sub.createdBy ?? '—'}
                                    </td>
                                  </tr>
                                ))
                              : []
                          return [parentRow, ...subRows]
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )
          })}
        </div>
        <FloatingXScrollbar containerRef={listScrollRef} />
        </>
      ) : view === 'board' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {statusGroups.map((g) => {
            const rows = g.tasks
            return (
              <div
                key={g.name}
                className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden"
              >
                <div className="px-4 py-2.5 bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] flex items-center gap-2">
                  <StatusPill name={g.name} color={g.color} />
                  <span className="text-sm text-[color:var(--color-ink-muted)] ml-auto">
                    {/* Same source of truth as the List sections — counts
                     *  subtasks by their own status (and the server's exact,
                     *  uncapped total when unfiltered), so Board columns and
                     *  List sections always agree. `rows.length` counted only
                     *  the top-level parent cards and undercounted. */}
                    {groupCount(g)}
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  {rows.map((t) => {
                    const subs = subtasksByParent.get(String(t.id)) ?? []
                    const hasSubs = subs.length > 0
                    const expanded = expandedParents.has(t.id)
                    return (
                      <div key={t.id}>
                        <div className="rounded-md border border-[color:var(--color-rule)] p-3 bg-[color:var(--color-card)]">
                          <div className="flex items-start gap-2">
                            <span
                              className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: t.statusColor ?? '#94a3b8' }}
                              aria-hidden="true"
                            />
                            {t.space?.id && t.list?.id ? (
                              <Link
                                to={`/app/spaces/${t.space.id}/lists/${t.list.id}?focusTask=${t.id}`}
                                className="text-[color:var(--color-ink-strong)] font-normal text-sm hover:underline cursor-pointer"
                                title="Open task in its list"
                              >
                                {t.name}
                              </Link>
                            ) : (
                              <p className="text-[color:var(--color-ink-strong)] font-normal text-sm">
                                {t.name}
                              </p>
                            )}
                          </div>
                          {t.department || t.startDate ? (
                            <p className="mt-1 ml-4 text-xs text-[color:var(--color-ink-muted)]">
                              {[
                                t.department,
                                t.startDate
                                  ? `Start ${taskDate(t.startDate, t.startDateHasTime)}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          ) : null}
                          {/* Due — colour-coded like the Space Board card
                           *  (overdue red / due today amber / done green). */}
                          <p
                            className="mt-0.5 ml-4 text-xs font-medium"
                            style={{ color: dueDateColor(t.dueDate, t.completedAt) }}
                          >
                            Due {taskDate(t.dueDate, t.dueDateHasTime) || '—'}
                          </p>
                          <div className="mt-2 ml-4 flex items-center justify-between gap-2">
                            {t.assignees && t.assignees.length > 0 ? (
                              <AvatarStack people={t.assignees} size={22} />
                            ) : t.assigneeName ? (
                              <AvatarStack people={[{ name: t.assigneeName }]} size={22} />
                            ) : (
                              <span className="text-xs text-[color:var(--color-ink-muted)]">
                                Unassigned
                              </span>
                            )}
                            <PriorityBadge priority={t.priority} />
                          </div>
                          {hasSubs ? (
                            <button
                              type="button"
                              onClick={() => toggleParent(t.id)}
                              aria-expanded={expanded}
                              title={
                                expanded ? 'Hide subtasks' : 'Show subtasks'
                              }
                              className="mt-2 ml-4 inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer"
                            >
                              <ChevronRight
                                size={12}
                                className={
                                  'transition-transform ' +
                                  (expanded ? 'rotate-90' : '')
                                }
                              />
                              <span>
                                {subs.length}{' '}
                                {subs.length === 1 ? 'subtask' : 'subtasks'}
                              </span>
                            </button>
                          ) : null}
                        </div>
                        {/* Subtask cards nested below parent. Slightly
                         *  muted background + accent left-border so the
                         *  parent/child relationship reads at a glance. */}
                        {hasSubs && expanded
                          ? subs.map((sub) => (
                              <div
                                key={sub.id}
                                className="rounded-md border border-[color:var(--color-rule)] border-l-2 border-l-[color:var(--color-accent)] p-2.5 mt-1.5 ml-4 bg-[color:var(--color-canvas)]"
                              >
                                <div className="flex items-start gap-2">
                                  <span
                                    className="mt-1 h-2 w-2 rounded-full shrink-0"
                                    style={{
                                      backgroundColor:
                                        sub.statusColor ?? '#94a3b8',
                                    }}
                                    aria-hidden="true"
                                  />
                                  {(sub.space?.id ?? t.space?.id) &&
                                  (sub.list?.id ?? t.list?.id) ? (
                                    <Link
                                      to={`/app/spaces/${sub.space?.id ?? t.space.id}/lists/${sub.list?.id ?? t.list.id}?focusTask=${sub.id}`}
                                      className="text-[color:var(--color-ink-strong)] text-xs font-normal hover:underline cursor-pointer"
                                      title="Open task in its list"
                                    >
                                      {sub.name}
                                    </Link>
                                  ) : (
                                    <p className="text-[color:var(--color-ink-strong)] text-xs font-normal">
                                      {sub.name}
                                    </p>
                                  )}
                                </div>
                                <p className="mt-1 ml-4 text-[11px] text-[color:var(--color-ink-muted)]">
                                  {[
                                    sub.department,
                                    sub.startDate
                                      ? `Start ${taskDate(sub.startDate, sub.startDateHasTime)}`
                                      : null,
                                    `Due ${taskDate(sub.dueDate, sub.dueDateHasTime)}`,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </p>
                                <div className="mt-1.5 ml-4 flex items-center justify-between gap-2">
                                  <span
                                    className="inline-flex items-center gap-1.5 text-[11px] text-[color:var(--color-ink)] min-w-0"
                                    title={sub.assigneeName ?? 'Unassigned'}
                                  >
                                    <Avatar name={sub.assigneeName} />
                                    <span className="truncate">
                                      {sub.assigneeName ?? 'Unassigned'}
                                    </span>
                                  </span>
                                  <PriorityBadge priority={sub.priority} />
                                </div>
                              </div>
                            ))
                          : null}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <TaskMonitorGantt tasks={filtered} onUpdateTask={onUpdateTask} />
      )}
      </>
      ) : null}
    </div>
  )
}
